/* eslint-disable import/no-named-as-default-member */
import React, { useCallback } from 'react';
import dayjs from 'dayjs';
import { UserOutlined } from '@ant-design/icons';
import PaqueryTable from '@paquery-team/lib-table';
import ViewPackageButton from 'components/viewPackageButton';
import { DEFAULT_PAGINATE } from 'constants/defaultValues';
import { PACKAGE } from 'constants/packages';
import { TableDateTimeFormat } from 'constants/dateFormats';
import ModalButton from 'components/modalButton';
import DeliveryProofsButton from 'components/deliveryProofsButton';
import useMarketplaces from 'redux/marketplaces';
import useStore from 'redux/store/index';
import {
  useFinalStatusOptions,
  useMarketplaceOptions,
  useStoreOptions,
} from 'hooks/useOptions';
import { marketplacesItemsSelector } from 'redux/marketplaces/selectors';
import { storeItemsSelector } from 'redux/store/selectors';
import usePackagesHistory from 'redux/packagesHistory';
import { useDispatch, useSelector } from 'react-redux';
import { selectGlobals } from 'redux/globals/selectors';
import {
  packagesHistoryLoadedSelector,
  packagesHistoryItemsSelector,
  packagesHistoryPaginationSelector,
} from 'redux/packagesHistory/selectors';
import { actions } from 'redux/packagesHistory/slice';
import { actions as storeActions } from 'redux/store/slice';

const statusStyle = (status) => {
  let bgColor;
  switch (status) {
    case PACKAGE.STATUSES.DELIVERED:
      bgColor = 'green';
      break;
    case PACKAGE.STATUSES.CANCELLED:
      bgColor = 'red';
      break;
    case PACKAGE.STATUSES.DAMAGED:
      bgColor = 'orange';
      break;
    case PACKAGE.STATUSES.RETURNED:
      bgColor = 'gold';
      break;
    default:
      break;
  }
  return {
    background: bgColor,
    padding: 4,
    borderRadius: '5px',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    color: 'white',
  };
};

const statusColumn = {
  title: 'Estado',
  render: (_, record) => (
    <span style={statusStyle(record.status)}>{record.statusDescription}</span>
  ),
};

const driverAvatar = (record) => (
  <ModalButton
    name={record.driverName}
    image={record.driverImage}
    icon={<UserOutlined />}
  />
);

const signatureAvatar = (record) => (
  <DeliveryProofsButton
    packageId={record.id}
    signatureImage={record.signatureImage}
  />
);

const externalCodeColumn = {
  title: 'Externo',
  dataIndex: 'externalCode',
  width: 130,
  render: (text) => <span style={{ wordBreak: 'break-word' }}>{text}</span>,
};

const destinationAddressColumn = {
  title: 'Destino',
  dataIndex: 'destinationAddress',
};

const deliveryTermColumn = {
  title: 'Plazo',
  dataIndex: 'deliveryTerm',
};

const contentColumn = {
  title: 'Contenido',
  dataIndex: 'content',
};

const arrivedAtPaqueryPointDateColumn = {
  title: 'Arribó a PaqueryPoint',
  dataIndex: 'arrivedAtPaqueryPointDate',
  align: 'center',
};

const deliveryDateColumn = {
  title: 'Fecha de entrega',
  dataIndex: 'deliveryDate',
};

const driverColumn = {
  title: 'PaQuer',
  align: 'center',
  render: (_, record) => driverAvatar(record),
};

const driverNameColumn = {
  title: 'Conductor',
  dataIndex: 'driverName',
};

const signatureColumn = {
  title: 'Firma',
  render: (_, record) => signatureAvatar(record),
};

const signatureNameColumn = {
  title: 'Firma',
  dataIndex: 'signatureName',
};

const viewPackageColumn = {
  align: 'center',
  render: (_, record) => <ViewPackageButton packageId={record.id} />,
};

const dataColumns = [
  externalCodeColumn,
  destinationAddressColumn,
  deliveryTermColumn,
  contentColumn,
  arrivedAtPaqueryPointDateColumn,
  deliveryDateColumn,
  signatureNameColumn,
  driverNameColumn,
  statusColumn,
];

const fullSizeColumns = [
  externalCodeColumn,
  destinationAddressColumn,
  deliveryTermColumn,
  contentColumn,
  driverColumn,
  arrivedAtPaqueryPointDateColumn,
  deliveryDateColumn,
  signatureColumn,
  statusColumn,
  viewPackageColumn,
];

const columnsSmallDevice = [externalCodeColumn];

const columnsMediumDevice = [
  externalCodeColumn,
  destinationAddressColumn,
  deliveryTermColumn,
];

const PackageHistory = () => {
  usePackagesHistory();
  useMarketplaces({ initialize: true });
  useStore();
  const dispatch = useDispatch();
  const globals = useSelector(selectGlobals);
  const marketplaces = useSelector(marketplacesItemsSelector);
  const marketplacesOptions = useMarketplaceOptions(marketplaces);
  const stores = useSelector(storeItemsSelector);
  const storesOptions = useStoreOptions(stores);
  const loaded = useSelector(packagesHistoryLoadedSelector);
  const items = useSelector(packagesHistoryItemsSelector);
  const pageable = useSelector(packagesHistoryPaginationSelector);
  const filteredFinalStatuses = useFinalStatusOptions();

  const searchCallback = useCallback(
    (searchText) => {
      dispatch(actions.updateSearch(searchText));
    },
    [dispatch],
  );
  const dateSelectionCallback = useCallback(
    (newDate) => {
      dispatch(actions.updateDate(newDate));
    },
    [dispatch],
  );
  const selectStatusCallback = useCallback(
    (newStatus) => {
      dispatch(actions.updateStatus(newStatus));
    },
    [dispatch],
  );
  const selectPackageTypeCallback = useCallback(
    (newPackageType) => {
      dispatch(actions.updatePackageType(newPackageType));
    },
    [dispatch],
  );

  const selectMarketplaceCallback = useCallback(
    (newMarketplace) => {
      dispatch(actions.updateMarketplace(newMarketplace));
      if (newMarketplace) dispatch(storeActions.all(newMarketplace));
    },
    [dispatch],
  );
  const selectStoreCallback = useCallback(
    (storeSelected) => {
      dispatch(actions.updateStore(storeSelected));
    },
    [dispatch],
  );
  const selectDeliveryTermCallback = useCallback(
    (newDeliveryTerm) => {
      dispatch(actions.updateDeliveryTerm(newDeliveryTerm));
    },
    [dispatch],
  );

  const searcher = {
    onSearching: searchCallback,
    placeholder: 'Código o destinatario',
    allowEmptySearch: true,
  };
  const selectors = [
    {
      onChange: selectMarketplaceCallback,
      placeholder: 'Marketplace',
      list: marketplacesOptions,
    },
    {
      onChange: selectStoreCallback,
      placeholder: 'Tienda',
      list: storesOptions,
    },
    {
      onChange: selectPackageTypeCallback,
      list: globals.packages.type,
      placeholder: 'Tipo de envio',
    },
    {
      list: filteredFinalStatuses,
      onChange: selectStatusCallback,
      placeholder: 'Estado',
    },
    {
      onChange: selectDeliveryTermCallback,
      placeholder: 'Plazo',
      list: globals.deliveryTerm,
    },
  ];
  const rangePicker = {
    onDateSelection: dateSelectionCallback,
    required: {
      value: true,
      message: 'La fecha es requerida',
    },
  };
  let filteredPackages = [];
  if (loaded && items) {
    filteredPackages = items.map((packet) => ({
      ...packet,
      key: packet.id,
      externalCode: packet.externalCode,
      destinationAddress:
        packet.shippingScheduleDestination.shippingAddress.addressDetail,
      deliveryTerm: globals.deliveryTerm.find(
        (term) => term.value === packet.deliveryTerm,
      )?.description,
      content: packet.caption,
      driverImage: packet.shippingScheduleDestination.driver
        ? packet.shippingScheduleDestination.driver.avatar
        : null,
      driverName: packet.shippingScheduleDestination.driver
        ? `${packet.shippingScheduleDestination.driver.name} ${packet.shippingScheduleDestination.driver.lastName}`
        : null,
      deliveryDate: packet.deliveryDate
        ? dayjs(packet.deliveryDate).tz().format(TableDateTimeFormat)
        : null,
      arrivedAtPaqueryPointDate: packet.arrivedAtPaqueryPointDate
        ? dayjs(packet.arrivedAtPaqueryPointDate)
            .tz()
            .format(TableDateTimeFormat)
        : null,
      signatureImage: packet.signatureImage,
      signatureName: packet.shippingScheduleDestination.name,
      statusDescription: globals.packages.status.find(
        (state) => state.value === packet.status,
      )?.name,
    }));
  }
  return (
    <PaqueryTable
      loading={!loaded}
      header={{
        searcher,
        selectors,
        rangePicker,
        refresh: () => dispatch(actions.refreshPage()),
        title: 'Histórico',
      }}
      onChangePaginate={(newPagination) =>
        dispatch(actions.updatePagination(newPagination))
      }
      usePackageRowColors
      dataSource={filteredPackages}
      paginate={pageable || DEFAULT_PAGINATE}
      dataColumns={dataColumns}
      colsForMediumDevice={columnsMediumDevice}
      colsForSmallDevice={columnsSmallDevice}
      colsForLargeDevice={fullSizeColumns}
    />
  );
};

export default PackageHistory;
